<template>
  <div>
    <!-- proposal.contractId -->
    <div class="mb-4 mt-6 text-2xl font-semibold">
      <span class="text-gray-500">#</span>
      <span>{{ proposal.contractId }}</span>
    </div>

    <form @submit.stop.prevent="form.handleSubmit()">
      <div class="grid grid-cols-2 gap-8">
        <!-- Issued on -->
        <div>
          <form.Field
            name="contractDate"
            :validators="{
              onBlur: yup.string().required('Issued on is required')
            }"
          >
            <template #default="{ field }">
              <BaseInputDate
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Issued on"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>

        <!--  Valid until -->
        <div>
          <form.Field
            name="contractValidDate"
            :validators="{
              onBlur: yup.string().required('Valid until is required')
            }"
          >
            <template #default="{ field }">
              <BaseInputDate
                :model-value="(field.state.value as string)"
                :name="field.name"
                label="Valid until"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @input="(e) => field.handleChange(e.target.value)"
              />
            </template>
          </form.Field>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-8">
        <!-- Quote for -->
        <div>
          <form.Field
            name="customerId"
            :validators="{
              onBlur: yup.string().required('Quote for is required')
            }"
          >
            <template #default="{ field }">
              <CustomerAutoComplete
                :model-value="field.state.value"
                :name="field.name"
                label="Quote for"
                :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                @blur="field.handleBlur"
                @update:model-value="(value) => field.handleChange(value)"
              />
            </template>
          </form.Field>
        </div>
      </div>

      <!-- original contract items -->
      <div class="mt-6">
        <div
          v-for="(lineItems, category) in getProposalItems"
          :key="category"
          class="mb-3"
        >
          <span class="font-semibold text-xl mb-3">{{ category }}</span>

          <div
            v-for="(lineItem, idx) in lineItems"
            :key="lineItem.id"
            :class="idx == lineItems.length - 1 ? 'border-b border-gray-200 py-4' : 'py-4'"
          >
            <div>
              <div class="grid grid-cols-5 gap-4">
                <div class="col-span-4 flex items-start">
                  <div
                    v-if="!lineItem.isCreditLineItem"
                    class="cursor-pointer mr-2 mt-4"
                    @click="lineItem.optional = !lineItem.optional"
                  >
                    <!-- optional badge -->
                    <BaseBadge
                      v-if="lineItem.optional"
                      variant="warning"
                    >
                      Optional
                    </BaseBadge>
                    <BaseBadge
                      v-else
                      variant="success"
                    >
                      Required
                    </BaseBadge>
                  </div>

                  <BaseCollapse :bg-color="'bg-withe'">
                    <template #header>
                      <div class="flex items-center">
                        <span class="font-bold">{{ lineItem.title }}</span>
                      </div>
                    </template>

                    <div class="text-justify" v-html="lineItem.scopeOfWork" />

                    <!-- attachments -->
                    <div class="flex flex-wrap gap-4 mt-4">
                      <div
                        v-for="file in lineItem.attachments"
                        :key="file"
                        class="relative w-[90px] h-[90px] rounded-lg border border-slate-200 flex items-center justify-center mt-2 bg-slate-100"
                      >
                        <img
                          v-if="isImage(file)"
                          :src="file"
                          class="object-cover rounded w-[90px] h-[90px] cursor-pointer"
                        >
                        <div
                          v-else
                          class="flex items-center justify-center w-full h-full text-center bg-slate-200 rounded-lg cursor-pointer"
                        >
                          <div class="text-dark font-weight-bold">
                            {{ fileTypeToString(file) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </BaseCollapse>
                </div>

                <!-- make optional dropdown -->

                <!-- totals -->
                <div class="flex flex-col gap-1 mt-4 text-sm">
                  <p class="grid grid-cols-2">
                    <span class="font-semibold">Total Price:</span>
                    <span>{{ currency(lineItem.totalPrice) }}</span>
                  </p>
                  <p class="grid grid-cols-2">
                    <span class="font-semibold">Quantity:</span>
                    <span>{{ lineItem.quantity }}</span>
                  </p>
                  <div class="flex flex-col justify-center">
                    <p class="grid grid-cols-2">
                      <span class="font-semibold">Cost:</span>
                      <span>{{ currency(lineItem.cost) }}</span>
                    </p>
                    <span class="block text-xs font-bold text-gray-700">Not visible to client</span>
                  </div>
                </div>
              </div>


            </div>
          </div>
        </div>
      </div>


      <!-- Terms and Conditions -->
      <div class="mt-6">
        <span class="text-lg font-bold block mb-2">Terms and Conditions</span>

        <form.Field
          name="termsConditions"
          :validators="{
            onChange: yup.string().transform((value) => value.replace(/<[^>]*>/g, '')).required('Terms and Conditions is required')
          }"
        >
          <template #default="{ field }">
            <Editor
              v-model="(field.state.value as string)"
              :name="field.name"
              class="min-h-28"
              @update:model-value="(value) => field.handleChange(value)"
            />
            <!-- errors -->
            <div v-if="field.state.meta.errors" class="text-red-500 text-sm mt-1">
              {{ field.state.meta.errors[0] }}
            </div>
          </template>
        </form.Field>
      </div>


      <!-- submit and cancel buttons -->
      <hr class="mt-6">
      <form.Subscribe>
        <template #default="{ canSubmit, isSubmitting }">
          <div class="grid items-center gap-4 mt-4" :class="{ 'grid-cols-2': props.autoSave && proposal.status === PROPOSAL_STATUS.DRAFT, 'grid-cols-1': !(props.autoSave && proposal.status === PROPOSAL_STATUS.DRAFT) }">
            <!-- last auto-saving indicator -->
            <div
              v-if="proposal.status === PROPOSAL_STATUS.DRAFT"
              class="text-sm text-gray-500"
            >
              <div
                v-if="props.autoSave"
                class="flex items-center gap-2"
              >
                <span>Last auto-saved at:</span>
                <span v-if="!autoSaving" class="font-bold">
                  {{ lastAutoSave ? lastAutoSave.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A' }}
                </span>
                <span v-if="autoSaving" class="i-heroicons-ellipsis-horizontal animate-pulse font-bold size-6 inline-block" />
              </div>

              <div v-else class="text-gray-500 font-bold">
                Auto save is disabled
              </div>
            </div>

            <div class="flex items-center justify-end gap-2 flex-1">
              <BaseButton
                variant="base"
                text="Cancel"
                :to="{ name: 'ShowProposal', params: { id: proposal.id } }"
                :disabled="isSubmitting"
                :class="{ 'cursor-not-allowed opacity-50': isSubmitting }"
              />

              <BaseButton
                variant="primary"
                text="Save"
                type="submit"
                :disabled="!canSubmit || isSubmitting"
                :class="{ 'cursor-not-allowed opacity-50': !canSubmit || isSubmitting }"
              />
            </div>
          </div>
        </template>
      </form.Subscribe>
    </form>
  </div>
</template>

<script lang="ts" setup>
import { Proposal, PROPOSAL_STATUS } from '@/models/proposal'
import { filter, contains, compact, extend } from 'underscore'
import BaseInputDate from '@/components/base/BaseInputDate.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import Editor from '@/components/TextEditor.vue'
import CustomerAutoComplete from '@/components/base/CustomerAutoComplete.vue'
import { Project } from '@/models/project'
import { Customer } from '@/models/customer'
import { Budget } from '@/models/budget'
import BaseCollapse from '@/components/base/BaseCollapse.vue'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import { computed, onBeforeUnmount, PropType, ref, watch } from 'vue'
import { currency, fileTypeToString, isImage, sortLineItemsByCategoryName, toNumber } from '@/utils'
import BaseBadge from '@/components/base/BaseBadge.vue'
import { useUnsavedChangesWarning } from '@/composables/useUnsavedChangesWarning'
import BudgetService from '@/services/BudgetService'
import ProposalService from '@/services/ProposalService'
import { useNotification } from '@kyvg/vue3-notification'
import { useQueryClient } from '@tanstack/vue-query'

const props = defineProps({
  project: {
    type: Object as PropType<Project>,
    required: true
  },
  customer: {
    type: Object as PropType<Customer>,
    required: true
  },
  budget: {
    type: Object as PropType<Budget>,
    required: true
  },
  model: {
    type: Object as PropType<Proposal>,
    required: true
  },
  autoSave: {
    type: Boolean,
    default: true
  },
  autoSaveInterval: {
    type: Number,
    default: 10
  },
  saveFunction: {
    type: Function,
    required: true
  }
})

const proposal = ref({ ...props.model })
const budgetToUpdate = ref({...JSON.parse(JSON.stringify(props.budget))} as Budget)
const hasUnsavedChanges = ref(true)
const { notify } = useNotification()
const queryClient = useQueryClient()

const form = useForm({
  defaultValues: proposal.value,
  validatorAdapter: yupValidator(),
  onSubmit: async ({ value }) => {
    const lineItemsIds = Object.values(getProposalItems.value).flat().map((item) => item.id)
    const updatedProposal = extend(value, { lineItems: lineItemsIds }) as Proposal
    updatedProposal.contractTotal = contractTotal.value

    await props.saveFunction({ proposal: updatedProposal, budget: budgetToUpdate.value })
    hasUnsavedChanges.value = false
  },
})

const contractTotal = computed(() => {
  const lineItemsIds = Object.values(getProposalItems.value).flat().map((item) => item.id)
  return filter(budgetToUpdate.value.lineItems, (item) => contains(lineItemsIds, item.id)).reduce((acc, item) => acc + toNumber(item.totalPrice), 0)
})


const getProposalItems = computed(() => {
  let items = []

  if (proposal.value.status === PROPOSAL_STATUS.DRAFT || proposal.value.status === PROPOSAL_STATUS.AWAITING_ACCEPTANCE) {
    items = compact(budgetToUpdate.value.lineItems)
  } else {
    items = filter(budgetToUpdate.value.lineItems, (item) => contains(proposal.value.lineItems, item.id))
  }

  const sortedItems = sortLineItemsByCategoryName(items)

  return sortedItems
})


let autoSaveTimeout: ReturnType<typeof setInterval>
const lastAutoSave = ref<Date | null>(null)
const autoSaving = ref(false)
const budgetService = new BudgetService()
const proposalService = new ProposalService()

watch(() => [proposal.value.status, props.autoSave], ([status, enabled]) => {
  clearInterval(autoSaveTimeout)
  if (status !== PROPOSAL_STATUS.DRAFT || !enabled) {
    return
  }

  autoSaveTimeout = setInterval(async () => {
    autoSaving.value = true
    lastAutoSave.value = null
    hasUnsavedChanges.value = true
    try {
      await budgetService.updateBudget(budgetToUpdate.value, [])
      const lineItemsIds = Object.values(getProposalItems.value).flat().map((item) => item.id)
      const proposalData = {
        ...form.useStore((state) => state.values).value,
        contractTotal: contractTotal.value,
        lineItems: lineItemsIds,
      }
      await proposalService.update(proposal.value.projectId, proposalData)
      lastAutoSave.value = new Date()
      hasUnsavedChanges.value = false
    } catch (error) {
      console.error('Auto save error:', error)
      notify({
        type: 'error',
        title: 'Auto save error',
        text: error.message || 'An error occurred while auto saving the proposal'
      })
    } finally {
      autoSaving.value = false
    }
  }, props.autoSaveInterval * 1000)
}, { immediate: true })


onBeforeUnmount(() => {
  clearInterval(autoSaveTimeout)
  if (lastAutoSave.value) {
    const lineItemsIds = Object.values(getProposalItems.value).flat().map((item) => item.id)
    const proposalData = {
      ...form.useStore((state) => state.values).value,
      contractTotal: contractTotal.value,
      lineItems: lineItemsIds,
    }
    queryClient.setQueryData(['proposal', proposal.value.id], proposalData)
    queryClient.setQueryData(['budget', budgetToUpdate.value.id], budgetToUpdate.value)
  }
})

// prevent user from navigating away without saving
useUnsavedChangesWarning(hasUnsavedChanges)

</script>
